import { useCallback, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import {
  applyVisibleSelection,
  getDisplayColumnOrder,
  getManageableColumnIds,
  LEADS_TABLE_VIEW_KEY,
  LeadsTableViewPreference,
  reorderColumnPreference,
  sanitizePreference,
} from 'components/elements/LeadsTable/constants';
import UserPreferencesAPI from 'services/API/UserPreferencesAPI';
import { useUser } from 'state/auth';

/**
 * Owns the leads-table column preference ({ order, visible }).
 *
 * Seeded from the user's server-side preference (delivered with the user
 * payload at app load), it updates the table instantly on every change and
 * persists back to the backend — column order on drag-drop, visibility when the
 * column selector closes. The local copy is the source of truth for the live
 * table; saving also syncs the user atom so remounts read fresh values.
 */
export function useLeadsTableColumnPreferences() {
  const { user, setUser } = useUser();

  const [preference, setPreference] = useState<LeadsTableViewPreference>(() =>
    sanitizePreference(
      user?.view_preferences?.[LEADS_TABLE_VIEW_KEY] as
        | Partial<LeadsTableViewPreference>
        | undefined,
    ),
  );

  const { mutate: savePreference } = useMutation((next: LeadsTableViewPreference) =>
    UserPreferencesAPI.saveViewPreference(LEADS_TABLE_VIEW_KEY, next),
  );

  const persist = useCallback(
    (next: LeadsTableViewPreference) => {
      savePreference(next);
      setUser((prev) =>
        prev
          ? {
              ...prev,
              view_preferences: {
                ...prev.view_preferences,
                [LEADS_TABLE_VIEW_KEY]: next,
              },
            }
          : prev,
      );
    },
    [savePreference, setUser],
  );

  const reorderColumns = useCallback(
    (fromId: string, toId: string, showStatus: boolean) => {
      setPreference((prev) => {
        const next = reorderColumnPreference(prev, fromId, toId, showStatus);
        if (next === prev) return prev;
        persist(next);

        return next;
      });
    },
    [persist],
  );

  // Apply a new visible selection immediately (table re-renders) without saving.
  const setVisibleSelection = useCallback(
    (selectedManageable: string[], showStatus: boolean) => {
      setPreference((prev) =>
        applyVisibleSelection(prev, showStatus, selectedManageable),
      );
    },
    [],
  );

  // Persist the current selection — call when the column selector closes.
  const persistVisibleSelection = useCallback(() => {
    setPreference((prev) => {
      persist(prev);

      return prev;
    });
  }, [persist]);

  const displayOrder = useCallback(
    (showStatus: boolean) => getDisplayColumnOrder(preference, showStatus),
    [preference],
  );

  const manageableColumns = useCallback(
    (showStatus: boolean) => getManageableColumnIds(preference.order, showStatus),
    [preference.order],
  );

  const visibleColumns = useMemo(() => new Set(preference.visible), [preference.visible]);

  return {
    displayOrder,
    reorderColumns,
    manageableColumns,
    visibleColumns,
    setVisibleSelection,
    persistVisibleSelection,
  };
}
